import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentMagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { getDateFnsLocale } from '@/utils/dateFnsLocale';
import {
  fetchUnifiedAuditLogs,
  fetchUnifiedAuditMeta,
  fetchUnifiedAuditTrends,
  searchUnifiedAuditUsers,
  type AuditUserOption,
  type UnifiedAuditLogDto,
  type UnifiedAuditMeta,
  type UnifiedAuditTrends,
} from '@/services/unifiedAuditService';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const PAGE_SIZE = 50;

const AUDIT_CHART_COLORS = [
  '#6366f1',
  '#22c55e',
  '#f97316',
  '#eab308',
  '#ec4899',
  '#14b8a6',
  '#8b5cf6',
  '#ef4444',
  '#64748b',
  '#0ea5e9',
];

function toLocalDatetimeInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parseLocalDatetimeInput(s: string): Date | null {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

const AuditloggingPage: React.FC = () => {
  const { translateWithFallback, currentLanguage } = useLanguage();
  const dfLocale = useMemo(() => getDateFnsLocale(currentLanguage.id), [currentLanguage.id]);

  const [meta, setMeta] = useState<UnifiedAuditMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(true);

  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [action, setAction] = useState('');
  const [tenantId, setTenantId] = useState<string>('');
  const [userQuery, setUserQuery] = useState('');
  const [userHits, setUserHits] = useState<AuditUserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<AuditUserOption | null>(null);
  const userSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [logs, setLogs] = useState<UnifiedAuditLogDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, pages: 0 });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  /** Bumps when filters are applied so we refetch even if `page` stays 1. */
  const [filterNonce, setFilterNonce] = useState(0);

  const [trends, setTrends] = useState<UnifiedAuditTrends | null>(null);
  const [trendsLoading, setTrendsLoading] = useState(false);

  const showTenantFilter = meta && meta.scope !== 'tenant';

  const fetchLogs = useCallback(
    async (pageArg: number) => {
      const from = parseLocalDatetimeInput(fromInput);
      const to = parseLocalDatetimeInput(toInput);
      if (!from || !to) {
        toast.error(translateWithFallback('pages.auditLogging.invalidRange', 'Invalid date range'));
        return;
      }
      try {
        setLoading(true);
        const data = await fetchUnifiedAuditLogs({
          from,
          to,
          page: pageArg,
          limit: PAGE_SIZE,
          resourceType: resourceType || undefined,
          action: action || undefined,
          tenantId: showTenantFilter && tenantId ? tenantId : undefined,
          userId: selectedUser?.id ?? undefined,
        });
        setLogs(data.logs);
        setPagination(data.pagination);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } };
        toast.error(
          (err.response?.data?.message as string | undefined) ??
            translateWithFallback('pages.auditLogging.error.loadLogs', 'Failed to load audit logs')
        );
      } finally {
        setLoading(false);
      }
    },
    [action, fromInput, resourceType, selectedUser, showTenantFilter, tenantId, toInput, translateWithFallback]
  );

  const fetchTrends = useCallback(async () => {
    const from = parseLocalDatetimeInput(fromInput);
    const to = parseLocalDatetimeInput(toInput);
    if (!from || !to) return;
    try {
      setTrendsLoading(true);
      const data = await fetchUnifiedAuditTrends({
        from,
        to,
        resourceType: resourceType || undefined,
        action: action || undefined,
        tenantId: showTenantFilter && tenantId ? tenantId : undefined,
        userId: selectedUser?.id ?? undefined,
      });
      setTrends(data);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(
        (err.response?.data?.message as string | undefined) ??
          translateWithFallback('pages.auditLogging.error.loadTrends', 'Failed to load audit trends')
      );
    } finally {
      setTrendsLoading(false);
    }
  }, [action, fromInput, resourceType, selectedUser, showTenantFilter, tenantId, toInput, translateWithFallback]);

  useEffect(() => {
    if (userSearchTimer.current) {
      clearTimeout(userSearchTimer.current);
    }
    if (selectedUser) {
      setUserHits([]);
      return;
    }
    const q = userQuery.trim();
    if (q.length < 2) {
      setUserHits([]);
      return;
    }
    userSearchTimer.current = setTimeout(() => {
      void (async () => {
        try {
          const hits = await searchUnifiedAuditUsers({
            q,
            tenantId: showTenantFilter && tenantId ? tenantId : undefined,
          });
          setUserHits(hits);
        } catch {
          setUserHits([]);
        }
      })();
    }, 300);
    return () => {
      if (userSearchTimer.current) clearTimeout(userSearchTimer.current);
    };
  }, [userQuery, selectedUser, showTenantFilter, tenantId]);

  useEffect(() => {
    setSelectedUser(null);
    setUserQuery('');
    setUserHits([]);
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setMetaLoading(true);
        const m = await fetchUnifiedAuditMeta();
        if (cancelled) return;
        setMeta(m);
        const to = new Date();
        const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
        const fi = toLocalDatetimeInputValue(from);
        const ti = toLocalDatetimeInputValue(to);
        setFromInput(fi);
        setToInput(ti);
        setPage(1);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } };
        toast.error(
          (err.response?.data?.message as string | undefined) ??
            translateWithFallback('pages.auditLogging.error.loadSettings', 'Failed to load audit log settings')
        );
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [translateWithFallback]);

  useEffect(() => {
    if (metaLoading || !meta) return;
    void fetchLogs(page);
  }, [page, meta, metaLoading, filterNonce, fetchLogs]);

  useEffect(() => {
    if (metaLoading || !meta) return;
    void fetchTrends();
  }, [meta, metaLoading, filterNonce, fetchTrends]);

  const tenantTrendChartRows = useMemo(() => {
    if (!trends?.labels.length || !trends.byTenant.length) return [];
    return trends.labels.map((day, i) => {
      const row: Record<string, string | number> = {
        day,
        label: format(parseISO(day), 'MMM d', { locale: dfLocale }),
      };
      for (const s of trends.byTenant) {
        row[s.key] = s.counts[i] ?? 0;
      }
      return row;
    });
  }, [trends, dfLocale]);

  const resourceTrendChartRows = useMemo(() => {
    if (!trends?.labels.length || !trends.byResourceType.length) return [];
    return trends.labels.map((day, i) => {
      const row: Record<string, string | number> = {
        day,
        label: format(parseISO(day), 'MMM d', { locale: dfLocale }),
      };
      for (const s of trends.byResourceType) {
        row[s.key] = s.counts[i] ?? 0;
      }
      return row;
    });
  }, [trends, dfLocale]);

  const trendXAxisAngle = (trends?.labels.length ?? 0) > 16 ? -40 : 0;
  const trendXAxisHeight = (trends?.labels.length ?? 0) > 16 ? 52 : 28;

  const applyPreset = (hours: number) => {
    const to = new Date();
    const from = new Date(to.getTime() - hours * 60 * 60 * 1000);
    setFromInput(toLocalDatetimeInputValue(from));
    setToInput(toLocalDatetimeInputValue(to));
    setPage(1);
    setFilterNonce((n) => n + 1);
  };

  const onSearch = () => {
    setPage(1);
    setFilterNonce((n) => n + 1);
  };

  const pickUser = (u: AuditUserOption) => {
    setSelectedUser(u);
    setUserQuery('');
    setUserHits([]);
    setPage(1);
    setFilterNonce((n) => n + 1);
  };

  const clearUser = () => {
    setSelectedUser(null);
    setUserQuery('');
    setUserHits([]);
    setPage(1);
    setFilterNonce((n) => n + 1);
  };

  const title = translateWithFallback('pages.auditLogging.title', 'Audit logging');
  const subtitle = translateWithFallback(
    'pages.auditLogging.viewerSubtitle',
    'Unified security and activity audit (RBAC / ABAC / domain events)'
  );
  
  return (
    <div className="min-h-screen bg-[var(--color-surface-bg)]">
      <div className="container mx-auto max-w-[1400px] px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <DocumentMagnifyingGlassIcon className="h-8 w-8 text-[var(--color-primary-text)]" />
            <div>
              <h1 className="text-3xl font-bold text-[var(--color-surface-text)]">{title}</h1>
              <p className="text-[var(--color-secondary-text)]">{subtitle}</p>
            </div>
          </div>
          <Button
            variant="outline"
            type="button"
            onClick={() => {
              void fetchLogs(page);
              void fetchTrends();
            }}
            disabled={loading || metaLoading}
          >
            <ArrowPathIcon className="mr-2 h-5 w-5" />
            {translateWithFallback('common.refresh', 'Refresh')}
          </Button>
        </div>

        <Card variant="outline" padding="md" className="mb-6">
          <CardContent>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-[var(--color-secondary-text)]" />
              <span className="font-medium text-[var(--color-surface-text)]">
                {translateWithFallback('pages.auditLogging.filters', 'Filters')}
              </span>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" type="button" onClick={() => applyPreset(1)}>
                  1h
                </Button>
                <Button variant="outline" size="sm" type="button" onClick={() => applyPreset(4)}>
                  4h
                </Button>
                <Button variant="outline" size="sm" type="button" onClick={() => applyPreset(12)}>
                  12h
                </Button>
                <Button variant="outline" size="sm" type="button" onClick={() => applyPreset(24)}>
                  24h
                </Button>
                <Button variant="outline" size="sm" type="button" onClick={() => applyPreset(24 * 7)}>
                  7d
                </Button>
                <Button variant="outline" size="sm" type="button" onClick={() => applyPreset(24 * 30)}>
                  30d
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--color-secondary-text)]">
                  {translateWithFallback('pages.auditLogging.from', 'From')}
                </label>
                <input
                  type="datetime-local"
                  value={fromInput}
                  onChange={(e) => setFromInput(e.target.value)}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-[var(--color-surface-text)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--color-secondary-text)]">
                  {translateWithFallback('pages.auditLogging.to', 'To')}
                </label>
                <input
                  type="datetime-local"
                  value={toInput}
                  onChange={(e) => setToInput(e.target.value)}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-[var(--color-surface-text)]"
                />
              </div>
              {showTenantFilter && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-[var(--color-secondary-text)]">
                    {translateWithFallback('pages.auditLogging.tenant', 'Tenant')}
                  </label>
                  <select
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-[var(--color-surface-text)]"
                  >
                    <option value="">{translateWithFallback('pages.auditLogging.allTenants', 'All allowed tenants')}</option>
                    {(meta?.tenantOptions ?? []).map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--color-secondary-text)]">
                  {translateWithFallback('pages.auditLogging.resourceType', 'Resource / event type')}
                </label>
                <select
                  value={resourceType}
                  onChange={(e) => setResourceType(e.target.value)}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-[var(--color-surface-text)]"
                >
                  <option value="">{translateWithFallback('pages.auditLogging.allTypes', 'All types')}</option>
                  {(meta?.resourceTypes ?? []).map((rt) => (
                    <option key={rt} value={rt}>
                      {rt}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--color-secondary-text)]">
                  {translateWithFallback('pages.auditLogging.action', 'Action')}
                </label>
                <select
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-[var(--color-surface-text)]"
                >
                  <option value="">{translateWithFallback('pages.auditLogging.allActions', 'All actions')}</option>
                  {(meta?.actions ?? []).map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button className="w-full" type="button" onClick={onSearch} disabled={loading}>
                  {translateWithFallback('common.apply', 'Apply')}
                </Button>
              </div>
            </div>

            <div className="mt-4 border-t border-[var(--color-border)] pt-4">
              <label className="mb-1 block text-sm font-medium text-[var(--color-secondary-text)]">
                {translateWithFallback('pages.auditLogging.userFilter', 'User')}
              </label>
              <p className="mb-2 text-xs text-[var(--color-secondary-text)]">
                {translateWithFallback(
                  'pages.auditLogging.userFilterHint',
                  'Type at least two characters to search by name or email. Tenant filter narrows the user list when set.'
                )}
              </p>
              {selectedUser ? (
                <div className="flex max-w-xl flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-sm text-[var(--color-surface-text)]">
                    {selectedUser.displayName
                      ? `${selectedUser.displayName} · ${selectedUser.email}`
                      : selectedUser.email}
                    <button
                      type="button"
                      className="rounded px-1 text-lg leading-none text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)]"
                      onClick={clearUser}
                      aria-label={translateWithFallback('pages.auditLogging.clearUser', 'Clear user filter')}
                    >
                      ×
                    </button>
                  </span>
                </div>
              ) : (
                <div className="relative max-w-xl">
                  <input
                    type="search"
                    autoComplete="off"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    placeholder={translateWithFallback(
                      'pages.auditLogging.userFilterPlaceholder',
                      'Search by name or email (min. 2 characters)…'
                    )}
                    className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-[var(--color-surface-text)]"
                  />
                  {userHits.length > 0 && (
                    <ul
                      className="absolute z-20 mt-1 max-h-52 w-full overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] py-1 shadow-lg"
                      role="listbox"
                    >
                      {userHits.map((u) => (
                        <li key={u.id} role="option">
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-muted)]"
                            onClick={() => pickUser(u)}
                          >
                            <span className="font-medium text-[var(--color-surface-text)]">
                              {u.displayName || u.email}
                            </span>
                            {u.displayName ? (
                              <span className="block text-xs text-[var(--color-secondary-text)]">{u.email}</span>
                            ) : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card variant="outline" padding="md" className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[var(--color-surface-text)]">
              {translateWithFallback('pages.auditLogging.trendsTitle', 'Event trends')}
            </CardTitle>
            <p className="text-sm text-[var(--color-secondary-text)]">
              {translateWithFallback(
                'pages.auditLogging.trendsSubtitle',
                'Daily volume for the same filters as the table (stacked areas).'
              )}
            </p>
          </CardHeader>
          <CardContent>
            {trendsLoading || metaLoading ? (
              <p className="text-[var(--color-secondary-text)]">{translateWithFallback('common.loading', 'Loading…')}</p>
            ) : (
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-2 text-sm font-medium text-[var(--color-surface-text)]">
                    {translateWithFallback('pages.auditLogging.trendsByTenant', 'By tenant')}
                  </h3>
                  {tenantTrendChartRows.length === 0 ? (
                    <p className="text-sm text-[var(--color-secondary-text)]">
                      {translateWithFallback('pages.auditLogging.trendsEmpty', 'No events in this range for the chart.')}
                    </p>
                  ) : (
                    <div className="h-[280px] w-full min-w-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={tenantTrendChartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: 'var(--color-secondary-text)' }}
                            axisLine={{ stroke: 'var(--color-border)' }}
                            angle={trendXAxisAngle}
                            textAnchor={trendXAxisAngle ? 'end' : 'middle'}
                            height={trendXAxisHeight}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: 'var(--color-secondary-text)' }}
                            axisLine={{ stroke: 'var(--color-border)' }}
                            allowDecimals={false}
                            width={44}
                          />
                          <Tooltip
                            contentStyle={{
                              background: 'var(--color-surface-bg)',
                              border: '1px solid var(--color-border)',
                              borderRadius: '8px',
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          {trends?.byTenant.map((s, idx) => (
                            <Area
                              key={s.key}
                              type="monotone"
                              dataKey={s.key}
                              name={s.label}
                              stackId="tenant"
                              stroke={AUDIT_CHART_COLORS[idx % AUDIT_CHART_COLORS.length]}
                              fill={AUDIT_CHART_COLORS[idx % AUDIT_CHART_COLORS.length]}
                              fillOpacity={0.85}
                            />
                          ))}
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-medium text-[var(--color-surface-text)]">
                    {translateWithFallback('pages.auditLogging.trendsByEventType', 'By event type')}
                  </h3>
                  {resourceTrendChartRows.length === 0 ? (
                    <p className="text-sm text-[var(--color-secondary-text)]">
                      {translateWithFallback('pages.auditLogging.trendsEmpty', 'No events in this range for the chart.')}
                    </p>
                  ) : (
                    <div className="h-[280px] w-full min-w-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={resourceTrendChartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: 'var(--color-secondary-text)' }}
                            axisLine={{ stroke: 'var(--color-border)' }}
                            angle={trendXAxisAngle}
                            textAnchor={trendXAxisAngle ? 'end' : 'middle'}
                            height={trendXAxisHeight}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: 'var(--color-secondary-text)' }}
                            axisLine={{ stroke: 'var(--color-border)' }}
                            allowDecimals={false}
                            width={44}
                          />
                          <Tooltip
                            contentStyle={{
                              background: 'var(--color-surface-bg)',
                              border: '1px solid var(--color-border)',
                              borderRadius: '8px',
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          {trends?.byResourceType.map((s, idx) => (
                            <Area
                              key={s.key}
                              type="monotone"
                              dataKey={s.key}
                              name={s.label}
                              stackId="resource"
                              stroke={AUDIT_CHART_COLORS[idx % AUDIT_CHART_COLORS.length]}
                              fill={AUDIT_CHART_COLORS[idx % AUDIT_CHART_COLORS.length]}
                              fillOpacity={0.85}
                            />
                          ))}
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card variant="outline" padding="md">
          <CardContent>
            {loading || metaLoading ? (
              <p className="text-[var(--color-secondary-text)]">{translateWithFallback('common.loading', 'Loading…')}</p>
            ) : (
              <>
                <div className="mb-4 text-sm text-[var(--color-secondary-text)]">
                  {translateWithFallback('pages.auditLogging.resultCount', 'Showing {count} of {total}')
                    .replace('{count}', String(logs.length))
                    .replace('{total}', String(pagination.total))}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-[var(--color-border)]">
                        <th className="p-2 font-medium text-[var(--color-secondary-text)]">
                          {translateWithFallback('pages.auditLogging.col.time', 'Time')}
                        </th>
                        {showTenantFilter && (
                          <th className="p-2 font-medium text-[var(--color-secondary-text)]">
                            {translateWithFallback('pages.auditLogging.col.tenant', 'Tenant')}
                          </th>
                        )}
                        <th className="p-2 font-medium text-[var(--color-secondary-text)]">
                          {translateWithFallback('pages.auditLogging.col.user', 'User')}
                        </th>
                        <th className="p-2 font-medium text-[var(--color-secondary-text)]">
                          {translateWithFallback('pages.auditLogging.col.type', 'Type')}
                        </th>
                        <th className="p-2 font-medium text-[var(--color-secondary-text)]">
                          {translateWithFallback('pages.auditLogging.col.action', 'Action')}
                        </th>
                        <th className="p-2 font-medium text-[var(--color-secondary-text)]">
                          {translateWithFallback('pages.auditLogging.col.decision', 'Decision')}
                        </th>
                        <th className="p-2 font-medium text-[var(--color-secondary-text)]">
                          {translateWithFallback('pages.auditLogging.col.details', 'Details')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.length === 0 ? (
                        <tr>
                          <td
                            colSpan={showTenantFilter ? 7 : 6}
                            className="p-6 text-center text-[var(--color-secondary-text)]"
                          >
                            {translateWithFallback('pages.auditLogging.empty', 'No audit entries in this range.')}
                          </td>
                        </tr>
                      ) : (
                        logs.map((row) => (
                          <React.Fragment key={row.id}>
                            <tr className="border-b border-[var(--color-border)] align-top hover:bg-[var(--color-surface-muted)]">
                              <td className="whitespace-nowrap p-2 text-[var(--color-surface-text)]">
                                {format(new Date(row.created_at), 'yyyy-MM-dd HH:mm:ss', { locale: dfLocale })}
                              </td>
                              {showTenantFilter && (
                                <td
                                  className="max-w-[140px] truncate p-2 text-[var(--color-surface-text)]"
                                  title={row.tenant_name ?? ''}
                                >
                                  {row.tenant_name ?? row.tenant_id ?? '—'}
                                </td>
                              )}
                              <td
                                className="max-w-[180px] truncate p-2 text-[var(--color-surface-text)]"
                                title={row.user_email ?? ''}
                              >
                                {row.user_email ?? row.user_id ?? '—'}
                              </td>
                              <td className="p-2 font-mono text-xs text-[var(--color-surface-text)]">
                                {row.resource_type}
                              </td>
                              <td className="p-2 font-mono text-xs text-[var(--color-surface-text)]">{row.action}</td>
                              <td className="p-2">
                                <span
                                  className={
                                    row.decision === 'DENY'
                                      ? 'rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800'
                                      : 'rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800'
                                  }
                                >
                                  {row.decision}
                                </span>
                              </td>
                              <td className="p-2">
                                <button
                                  type="button"
                                  className="text-xs font-medium text-[var(--color-primary-text)] underline underline-offset-2 hover:opacity-90"
                                  onClick={() =>
                                    setExpanded((prev) => ({ ...prev, [row.id]: !prev[row.id] }))
                                  }
                                >
                                  {expanded[row.id]
                                    ? translateWithFallback('pages.auditLogging.hideMeta', 'Hide')
                                    : translateWithFallback('pages.auditLogging.showMeta', 'JSON')}
                                </button>
                              </td>
                            </tr>
                            {expanded[row.id] && (
                              <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]">
                                <td colSpan={showTenantFilter ? 7 : 6} className="p-3">
                                  <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3 font-mono text-xs text-[var(--color-surface-text)]">
                                    {JSON.stringify(
                                      {
                                        id: row.id,
                                        resource_id: row.resource_id,
                                        access_control_type: row.access_control_type,
                                        message: row.message,
                                        ip_address: row.ip_address,
                                        metadata: row.metadata,
                                      },
                                      null,
                                      2
                                    )}
                                  </pre>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {pagination.pages > 1 && (
                  <div className="mt-6 flex items-center justify-between gap-4">
                    <p className="text-sm text-[var(--color-secondary-text)]">
                      {translateWithFallback('common.page', 'Page')} {pagination.page} / {pagination.pages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        disabled={page <= 1 || loading}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        disabled={page >= pagination.pages || loading}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuditloggingPage;
